import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import {
  AppInventoryOption,
  AppInventoryVersion,
  type AppInventoryVersionType,
} from '@openx/types/targeting/appInventory.ts';
import {
  type InstanceProps,
  type InventoryAndContentState,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingDisabledState } from '../../../utils/hooks/useTargetingDisabledState.ts';
import type { InvalidItemsError } from '../../shared/BulkAdd/types.ts';
import { EmptyFilterMessage } from '../../shared/EmptyFilterMessage/EmptyFilterMessage.tsx';
import { useAppInventoryItems } from '../../shared/InventoryAndContent/hooks/useAppInventoryItems.ts';
import { setContentInterDimentionOperator } from '../../shared/InventoryAndContent/inventoryHelpers.ts';
import { Switch } from '../../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import type {
  AppInventoryComparisonTypeProps,
  SetInventoryListItemsDispatch,
} from '../InventoryAndContentTopBox/constants.ts';
import { AppInventoryItemsSection } from './AppInventoryItemsSection.tsx';
import { getInventoryAndContentOptions } from './getInventoryAndContentOptions.tsx';
import { getInventoriesWithSettings, isAppBundleIdPayload, isAppInventoryPayload } from './utils.ts';

type InventoryAndContentItemsProps = TargetingItemsProps<InventoryAndContentState> &
  AppInventoryComparisonTypeProps & {
    inputInstanceValue?: InstanceProps | null;
    isBefInstance: boolean;
    appBundleIdDuplicateErrors: InvalidItemsError | null;
    setInventoryListItems?: SetInventoryListItemsDispatch;
    inventoryListItems?: InventoryListByIdAPI['values'];
    inventoryVersion?: AppInventoryVersionType;
  };

export const InventoryAndContentItems = ({
  targetingParams,
  dispatchInventory = noop,
  readonly,
  instanceState,
  inputInstanceValue = null,
  isBefInstance,
  accountUid = '',
  appBundleIdDuplicateErrors,
  setInventoryListItems = noop,
  inventoryListItems = [],
  // App Inventory specific props
  appNameComparisonType,
  setAppNameComparisonType,
  bundleIdComparisonType,
  setBundleIdComparisonType,
  inventoryVersion,
  field,
}: InventoryAndContentItemsProps) => {
  const { t } = useTranslation();

  const { errorReasonMessage } = useTargetingDisabledState(field);
  const { errorReasonMessage: pageUrlErrorReasonMessage } = useTargetingDisabledState(
    field ? `${field}.${InventoryContentOption.PAGE_URL}` : undefined
  );
  const { errorReasonMessage: keywordsErrorReasonMessage } = useTargetingDisabledState(
    field ? `${field}.${InventoryContentOption.KEYWORDS}` : undefined
  );

  const getDisabledReasonMessage = useCallback(
    (option: InventoryContentOption) => {
      if (option === InventoryContentOption.PAGE_URL) return pageUrlErrorReasonMessage;
      if (option === InventoryContentOption.KEYWORDS) return keywordsErrorReasonMessage;
      return errorReasonMessage;
    },
    [errorReasonMessage, keywordsErrorReasonMessage, pageUrlErrorReasonMessage]
  );

  const inventoriesWithSettings = getInventoriesWithSettings(targetingParams);
  const inventoriesLength = inventoriesWithSettings.length;
  const showSwitch = !(readonly || typeof readonly === 'undefined' || inventoriesWithSettings.length === 0);
  const switchState = targetingParams[InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR] === Criteria.ANY;

  const inventoryAndContentBase = getInventoryAndContentOptions({
    dispatchInventory,
    readonly,
    targetingParams,
    accountUid,
    appBundleIdDuplicateErrors,
    setInventoryListItems,
    inventoryListItems,
    switchState,
    instanceState,
    inputInstanceValue,
    isBefInstance,
    getDisabledReasonMessage,
  });

  const inventoryAndContent =
    inventoryVersion === AppInventoryVersion.APP_INVENTORY_EXCLUDED_V1
      ? Object.fromEntries(
          Object.entries(inventoryAndContentBase).filter(
            ([key]) => key !== AppInventoryOption.APP_BUNDLE_ID && key !== AppInventoryOption.APP_GROUP
          )
        )
      : inventoryAndContentBase;

  const {
    shouldShowSwitch,
    canUseAppInventorySection,
    effectiveAppNameComparisonType,
    effectiveSetAppNameComparisonType,
    effectiveBundleIdComparisonType,
    effectiveSetBundleIdComparisonType,
    filteredInventoriesLength,
    filteredInventories,
  } = useAppInventoryItems({
    showSwitch,
    targetingParams,
    inventoriesWithSettings: inventoriesWithSettings as InventoryContentOption[],
    appNameComparisonType,
    setAppNameComparisonType,
    bundleIdComparisonType,
    setBundleIdComparisonType,
  });

  return (
    <Grid>
      {shouldShowSwitch && (
        <Box sx={{ marginTop: theme => theme.spacing(3) }}>
          <Switch
            switchState={switchState}
            onSwitchChange={() => {
              dispatchInventory(setContentInterDimentionOperator(switchState ? Criteria.ALL : Criteria.ANY));
            }}
            switchOffLabel={t('MATCH ALL')}
            switchOnLabel={t('MATCH ANY')}
          />
        </Box>
      )}

      {inventoriesLength ? (
        <Grid>
          {/* Render App Inventory with new UI (two sections with toggles) - only when all required props are available */}
          {canUseAppInventorySection &&
            isAppInventoryPayload(targetingParams[InventoryContentOption.APP_GROUP]) &&
            isAppBundleIdPayload(targetingParams[InventoryContentOption.APP_BUNDLE_ID]) && (
              <AppInventoryItemsSection
                field={field}
                appInventoryContent={targetingParams[InventoryContentOption.APP_GROUP]}
                appBundleIdContent={targetingParams[InventoryContentOption.APP_BUNDLE_ID]}
                dispatchInventory={dispatchInventory}
                readonly={!!readonly}
                appNameComparisonType={effectiveAppNameComparisonType}
                setAppNameComparisonType={effectiveSetAppNameComparisonType}
                bundleIdComparisonType={effectiveBundleIdComparisonType}
                setBundleIdComparisonType={effectiveSetBundleIdComparisonType}
                isNotLastItem={filteredInventoriesLength > 0}
                switchState={switchState}
                showDimensionChipLine={true}
                accountUid={accountUid}
                inventoryListItems={inventoryListItems}
                setInventoryListItems={setInventoryListItems}
              />
            )}

          {/* Render other inventory types normally */}
          {filteredInventories.map((inventory, index) =>
            inventoryAndContent[inventory](filteredInventoriesLength - 1 > index, true)
          )}
        </Grid>
      ) : (
        <EmptyFilterMessage text={t('No filters applied.')} />
      )}
    </Grid>
  );
};
